import React from 'react';
import { useNavigate } from 'react-router-dom';

const CHECKOUT_URL =
  'https://latexlab.lemonsqueezy.com/checkout/buy/1a74bd46-1574-43f3-b0c5-e827847c6462?desc=0';

const FREE_FEATURES = [
  { text: '20 compilations / month', included: true },
  { text: '3 templates', included: true },
  { text: 'Single file projects', included: true },
  { text: 'Community support', included: true },
  { text: 'Unlimited compilations', included: false },
  { text: '30+ professional templates', included: false },
  { text: 'Multi-file projects', included: false },
  { text: 'Real-time collaboration', included: false },
];

const PRO_FEATURES = [
  { text: 'Unlimited compilations', included: true },
  { text: '30+ professional templates', included: true, note: 'IEEE, ACM, Springer, Elsevier' },
  { text: 'Multi-file projects', included: true },
  { text: 'Real-time collaboration', included: true },
  { text: 'Priority support', included: true },
  { text: 'Word → LaTeX converter', included: true, badge: 'Coming Soon' },
];

function CheckIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
      <circle cx="10" cy="10" r="10" fill="#22c55e" />
      <path d="M6 10.5l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CrossIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
      <circle cx="10" cy="10" r="10" fill="#e2e8f0" />
      <path d="M7 7l6 6M13 7l-6 6" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

export default function PricingPage({ user }) {
  const navigate = useNavigate();
  const isFreeUser = user && !user.isPro;

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h1 style={styles.title}>Simple, Transparent Pricing</h1>
        <p style={styles.subtitle}>
          Start for free. Upgrade when you need more power.
        </p>
      </div>

      <div style={styles.cardsRow}>
        {/* FREE CARD */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <span style={styles.planLabel}>FREE</span>
            <div style={styles.price}>
              <span style={styles.priceAmount}>$0</span>
              <span style={styles.pricePer}>&nbsp;/ forever</span>
            </div>
            <p style={styles.cardDesc}>Everything you need to get started with LaTeX.</p>
          </div>

          <ul style={styles.featureList}>
            {FREE_FEATURES.map((f) => (
              <li key={f.text} style={styles.featureItem}>
                {f.included ? <CheckIcon /> : <CrossIcon />}
                <span style={{ color: f.included ? '#1e293b' : '#94a3b8' }}>{f.text}</span>
              </li>
            ))}
          </ul>

          <button
            style={styles.btnGray}
            disabled={isFreeUser}
            onClick={() => !user && navigate('/register')}
          >
            {isFreeUser ? 'Current Plan' : 'Get Started Free'}
          </button>
        </div>

        {/* PRO CARD */}
        <div style={styles.cardPro}>
          <div style={styles.proBadge}>Most Popular</div>
          <div style={styles.cardHeader}>
            <span style={{ ...styles.planLabel, color: '#16a34a' }}>PRO</span>
            <div style={styles.price}>
              <span style={styles.priceAmount}>$3</span>
              <span style={styles.pricePer}>&nbsp;/ month</span>
            </div>
            <p style={styles.cardDesc}>Unlock the full LaTeXLaB experience.</p>
          </div>

          <ul style={styles.featureList}>
            {PRO_FEATURES.map((f) => (
              <li key={f.text} style={styles.featureItem}>
                <CheckIcon />
                <span style={{ color: '#1e293b' }}>
                  {f.text}
                  {f.note && <span style={styles.noteText}> — {f.note}</span>}
                  {f.badge && <span style={styles.comingSoonBadge}>{f.badge}</span>}
                </span>
              </li>
            ))}
          </ul>

          {user?.isPro ? (
            <button style={styles.btnGray} disabled>Current Plan</button>
          ) : (
            <a
              href={CHECKOUT_URL}
              target="_blank"
              rel="noreferrer"
              style={styles.btnGreen}
            >
              Upgrade Now
            </a>
          )}
        </div>
      </div>

      <p style={styles.footer}>
        Questions?{' '}
        <a href="mailto:latexlab2026@gmail.com" style={styles.link}>
          Contact us
        </a>
      </p>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#f8fafc',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '60px 20px 80px',
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
  },
  header: {
    textAlign: 'center',
    marginBottom: '48px',
  },
  title: {
    fontSize: '2.4rem',
    fontWeight: 800,
    color: '#0f172a',
    margin: '0 0 12px',
  },
  subtitle: {
    fontSize: '1.1rem',
    color: '#64748b',
    margin: 0,
  },
  cardsRow: {
    display: 'flex',
    gap: '28px',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'flex-start',
    width: '100%',
    maxWidth: '820px',
  },
  card: {
    background: '#ffffff',
    border: '2px solid #e2e8f0',
    borderRadius: '20px',
    padding: '36px 32px',
    flex: '1 1 320px',
    maxWidth: '360px',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  cardPro: {
    background: '#ffffff',
    border: '2px solid #22c55e',
    borderRadius: '20px',
    padding: '36px 32px',
    flex: '1 1 320px',
    maxWidth: '360px',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    position: 'relative',
    boxShadow: '0 8px 32px rgba(34,197,94,0.12)',
  },
  proBadge: {
    position: 'absolute',
    top: '-14px',
    left: '50%',
    transform: 'translateX(-50%)',
    background: '#22c55e',
    color: '#ffffff',
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.05em',
    padding: '4px 16px',
    borderRadius: '999px',
    whiteSpace: 'nowrap',
  },
  cardHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  planLabel: {
    fontSize: '0.85rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    color: '#64748b',
    textTransform: 'uppercase',
  },
  price: {
    display: 'flex',
    alignItems: 'baseline',
  },
  priceAmount: {
    fontSize: '2.8rem',
    fontWeight: 800,
    color: '#0f172a',
    lineHeight: 1,
  },
  pricePer: {
    fontSize: '1rem',
    color: '#64748b',
    fontWeight: 500,
  },
  cardDesc: {
    fontSize: '0.9rem',
    color: '#64748b',
    margin: 0,
  },
  featureList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    flex: 1,
  },
  featureItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '0.93rem',
  },
  noteText: {
    color: '#64748b',
    fontSize: '0.82rem',
  },
  comingSoonBadge: {
    marginLeft: '8px',
    background: '#fef9c3',
    color: '#854d0e',
    fontSize: '0.7rem',
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: '999px',
    verticalAlign: 'middle',
  },
  btnGray: {
    display: 'block',
    width: '100%',
    padding: '14px',
    background: '#f1f5f9',
    color: '#64748b',
    border: 'none',
    borderRadius: '10px',
    fontSize: '1rem',
    fontWeight: 700,
    cursor: 'default',
    textAlign: 'center',
  },
  btnGreen: {
    display: 'block',
    width: '100%',
    padding: '14px',
    background: '#22c55e',
    color: '#ffffff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '1rem',
    fontWeight: 700,
    cursor: 'pointer',
    textAlign: 'center',
    textDecoration: 'none',
    transition: 'background 0.2s',
  },
  footer: {
    marginTop: '48px',
    color: '#64748b',
    fontSize: '0.95rem',
  },
  link: {
    color: '#22c55e',
    textDecoration: 'none',
    fontWeight: 600,
  },
};
